// ============================================================================
// POKER COACH PAGE v2.0 - ENTERPRISE LEVEL
// ============================================================================
// AI Chat interface for poker coaching with deep statistical analysis,
// personalized feedback, and intelligent query handling.
// Automatically loads latest session for context-aware responses.
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Divider,
  Badge,
  Grid,
  Collapse,
  Spinner,
  Alert,
  AlertIcon,
  Progress,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Send, 
  MessageCircle, 
  BookOpen, 
  Target, 
  Calculator, 
  ChevronRight,
  ChevronDown,
  Lightbulb,
  ArrowLeft,
  PlayCircle,
  AlertTriangle,
  TrendingUp,
  Award,
  Brain,
  BarChart2,
  RefreshCw,
  Grid3x3,
} from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import {
  pokerCoach,
  parseQuery,
  generateResponse,
  evaluateScenarioResponse,
  detectLeaks,
  generateImprovementPlan,
  classifyPlayerType,
  OPTIMAL_STATS,
  type CoachResponse,
  type Leak,
  type ImprovementPlan,
} from '../services/training/pokerCoachService';
import {
  type Scenario,
  formatScenarioForDisplay,
} from '../services/training/scenarioLibrary';
import {
  getTrainingReport,
  getUserTrainingReports,
  type TrainingReport,
} from '../services/training';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// TYPES
// ============================================================================

interface Message {
  id: string;
  role: 'user' | 'coach';
  content: string;
  timestamp: number;
  response?: CoachResponse;
  scenario?: Scenario;
}

// ============================================================================
// COMPONENT
// ============================================================================

const PokerCoachPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeScenario, setActiveScenario] = useState<Scenario | null>(null);
  const [sessionReport, setSessionReport] = useState<TrainingReport | null>(null);
  const [latestReport, setLatestReport] = useState<TrainingReport | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(true);
  const [showSessionPanel, setShowSessionPanel] = useState(false);
  
  // Load latest report on mount
  useEffect(() => {
    if (currentUser?.uid) {
      loadLatestReport();
    } else {
      setIsLoadingReport(false);
    }
  }, [currentUser?.uid]);
  
  // Load specific report if provided in URL. The ref guard keeps StrictMode
  // double-invokes and dep refires from posting the analysis twice.
  const loadedReportIdRef = useRef<string | null>(null);
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId && loadedReportIdRef.current !== reportId) {
      loadedReportIdRef.current = reportId;
      loadSessionReport(reportId);
    }
  }, [searchParams]);
  
  // Initial greeting with session context
  useEffect(() => {
    if (messages.length === 0 && !isLoadingReport) {
      // Arrived from a report page: the detailed session analysis is being
      // posted separately - keep the greeting to a one-line welcome instead
      // of a second stats dump.
      if (searchParams.get('report')) {
        setMessages([{
          id: 'welcome',
          role: 'coach',
          timestamp: Date.now(),
          content: '',
          response: {
            type: 'text',
            title: 'Poker Coach',
            content: 'Pulling up your session below. Ask me about any hand, any decision, or any term you do not recognize.',
            followUpQuestions: ['What was my biggest mistake?', 'Give me a practice scenario', 'What should I work on next?'],
          },
        }]);
        return;
      }
      const report = sessionReport || latestReport;
      const hasSession = !!report;
      
      let greetingContent = '';
      let followUps: string[] = [];
      
      if (hasSession) {
        const playerType = classifyPlayerType(
          report!.stats.vpip,
          report!.stats.pfr,
          report!.stats.aggressionFactor
        );
        const leaks = detectLeaks(report!);
        const criticalLeaks = leaks.filter(l => l.severity === 'critical');
        
        greetingContent = `Hey! I've loaded your latest session (Score: **${report!.overallScore.toFixed(1)}/10**).\n\n`;
        greetingContent += `**Player Profile:** ${playerType.label}\n`;
        
        if (criticalLeaks.length > 0) {
          greetingContent += `\n **I found ${criticalLeaks.length} critical issue${criticalLeaks.length > 1 ? 's' : ''}:**\n`;

          criticalLeaks.slice(0, 2).forEach(leak => {
            greetingContent += `• ${leak.title}\n`;
          });
          greetingContent += '\nWant me to explain how to fix these?';
          followUps = ['Find all my leaks', 'Create improvement plan', 'Practice my weak area'];
        } else if (leaks.length > 0) {
          greetingContent += `\n **Areas to improve:** ${leaks.slice(0, 2).map(l => l.title).join(', ')}\n`;

          greetingContent += '\nAsk me anything about your play!';
          followUps = ['Review my session', 'What should I work on?', 'Give me a scenario'];
        } else {
          greetingContent += '\n Your stats look solid! Keep up the good work.\n';

          greetingContent += '\nI can help you fine-tune your game or practice specific spots.';
          followUps = ['Create improvement plan', 'Give me an advanced scenario', 'BTN strategy'];
        }
      } else {
        greetingContent = 'Hey! I\'m your poker coach. I can help you learn strategy, practice scenarios, and analyze your play.\n\n' +
          '**Try asking me:**\n' +
          '• "What is pot odds?"\n' +
          '• "Give me a bluffing scenario"\n' +
          '• "How do I beat calling stations?"\n\n' +
          ' *Complete a training session for personalized feedback!*';

        followUps = ['Beginner tips', 'Give me a scenario', 'What is VPIP?'];
      }
      
      const greeting: Message = {
        id: 'welcome',
        role: 'coach',
        timestamp: Date.now(),
        content: '',
        response: {
          type: 'text',
          title: 'Poker Coach',

          content: greetingContent,
          followUpQuestions: followUps,
        },
      };
      setMessages([greeting]);
    }
  }, [isLoadingReport, sessionReport, latestReport]);
  
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Never let a stalled fetch strand the coach on the loading spinner:
  // if the report query doesn't settle quickly, fall through to the
  // generic greeting - the session data is a bonus, not a requirement.
  const withTimeout = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
    Promise.race([p, new Promise<T>(res => setTimeout(() => res(fallback), ms))]);

  const loadLatestReport = async () => {
    if (!currentUser?.uid) return;
    setIsLoadingReport(true);
    try {
      const reports = await withTimeout(getUserTrainingReports(currentUser.uid, 1), 6000, []);
      if (reports.length > 0) {
        const fullReport = await withTimeout(getTrainingReport(reports[0].reportId), 6000, null);
        if (fullReport) {
          setLatestReport(fullReport);
        }
      }
    } catch (err) {
      console.error('Failed to load latest report:', err);
    } finally {
      setIsLoadingReport(false);
    }
  };
  
  const loadSessionReport = async (reportId: string) => {
    try {
      const report = await withTimeout(getTrainingReport(reportId), 6000, null);
      if (report) {
        setSessionReport(report);
        // Add message about loaded session
        const sessionMsg: Message = {
          id: `session-${Date.now()}`,
          role: 'coach',
          timestamp: Date.now(),
          content: '',
          response: generateResponse(
            { type: 'session_review', originalQuery: '', extractedEntities: {}, confidence: 1 },
            { sessionReport: report }
          ),
        };
        setMessages(prev => [...prev, sessionMsg]);
      }
    } catch (err) {
      console.error('Failed to load session report:', err);
    }
  };
  
  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input,
      timestamp: Date.now(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    // Small delay for feel
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Parse and generate response with session context
    const parsed = parseQuery(input);
    const report = sessionReport || latestReport;
    const response = generateResponse(parsed, {
      sessionReport: report || undefined,
      latestReport: report || undefined,
    });
    
    const coachMessage: Message = {
      id: `coach-${Date.now()}`,
      role: 'coach',
      content: '',
      timestamp: Date.now(),
      response,
      scenario: response.scenario,
    };
    
    // If it's a scenario, set it as active
    if (response.scenario) {
      setActiveScenario(response.scenario);
    }
    
    setMessages(prev => [...prev, coachMessage]);
    setIsLoading(false);
    
    // Focus input after response
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [input, isLoading, sessionReport, latestReport]);
  
  const handleQuickAction = useCallback((action: string) => {
    setInput(action);
    setTimeout(() => {
      setInput(action);
      handleSend();
    }, 100);
  }, [handleSend]);
  
  const handleScenarioAnswer = useCallback((action: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all-in') => {
    if (!activeScenario) return;
    
    // User's answer
    const userAnswer: Message = {
      id: `answer-${Date.now()}`,
      role: 'user',
      content: `I ${action}`,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, userAnswer]);
    
    // Evaluate
    const evaluation = evaluateScenarioResponse(activeScenario, action);
    
    const feedbackMessage: Message = {
      id: `feedback-${Date.now()}`,
      role: 'coach',
      content: '',
      timestamp: Date.now(),
      response: {
        type: 'text',
        title: evaluation.correct ? ' Correct!' : ' Not Quite',

        content: evaluation.feedback + '\n\n**Key Lesson:** ' + evaluation.lesson,
        followUpQuestions: [
          'Give me another scenario',
          'Explain this concept more',
          'Practice something else',
        ],
      },
    };
    
    setMessages(prev => [...prev, feedbackMessage]);
    setActiveScenario(null);
  }, [activeScenario]);
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const report = sessionReport || latestReport;
  
  return (
    <Container maxW="4xl" py={6} pl={{ base: 4, md: 24 }} pb={{ base: 24, md: 8 }} h="100vh">
      <VStack h="100%" spacing={4} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <HStack spacing={3}>
            <IconButton
              aria-label="Back"
              icon={<ArrowLeft />}
              variant="ghost"
              color="gray.400"
              onClick={() => navigate('/training')}
            />
            <Box>
              <Heading size="md" color={POKER_COLORS.cyan}>
                 Poker AI Coach

              </Heading>
              <Text color="gray.500" fontSize="xs">
                Ask questions • Practice scenarios • Review plays
              </Text>
            </Box>
          </HStack>
          
          {/* Session Status Badge */}
          {report && (
            <HStack spacing={2}>
              <Badge
                bg={`${POKER_COLORS.darkGreen}22`}
                color={POKER_COLORS.darkGreen}
                px={2}
                py={1}
                borderRadius="md"
                cursor="pointer"
                onClick={() => setShowSessionPanel(!showSessionPanel)}
              >
                <HStack spacing={1}>
                  <BarChart2 size={12} />
                  <Text fontSize="xs">Session: {report.overallScore.toFixed(1)}/10</Text>
                  {showSessionPanel ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </HStack>
              </Badge>
            </HStack>
          )}
        </HStack>
        
        {/* Session Panel (Collapsible) */}
        <Collapse in={showSessionPanel && !!report}>
          {report && <SessionStatsPanel report={report} />}
        </Collapse>
        
        {/* Messages Area */}
        <Box
          flex={1}
          overflowY="auto"
          px={2}
          css={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: POKER_COLORS.cyan + '44', borderRadius: '2px' },
          }}
        >
          {isLoadingReport ? (
            <VStack py={8} spacing={4}>
              <Spinner color={POKER_COLORS.cyan} />
              <Text color="gray.500" fontSize="sm">Loading your session data...</Text>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch" py={2}>
              {messages.map(message => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  onQuickAction={handleQuickAction}
                  onScenarioAnswer={handleScenarioAnswer}
                  activeScenario={activeScenario}
                />
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <Box maxW="95%">
                  <PokerCard p={4}>
                    <HStack spacing={3}>
                      <Spinner size="sm" color={POKER_COLORS.cyan} />
                      <Text color="gray.400" fontSize="sm">Thinking...</Text>
                    </HStack>
                  </PokerCard>
                </Box>
              )}
              
              <div ref={messagesEndRef} />
            </VStack>
          )}
        </Box>
        
        {/* Quick Actions */}
        {!activeScenario && (
          <HStack spacing={2} flexWrap="wrap" rowGap={2} py={2} px={1}>
            <QuickButton onClick={() => handleQuickAction('Review my session')}>
              <BarChart2 size={14} /> Review Session
            </QuickButton>
            <QuickButton onClick={() => handleQuickAction('Find my leaks')}>
              <AlertTriangle size={14} /> Find Leaks
            </QuickButton>
            <QuickButton onClick={() => handleQuickAction('Give me a scenario')}>
              <Target size={14} /> Practice
            </QuickButton>
            <QuickButton onClick={() => handleQuickAction('Create improvement plan')}>
              <TrendingUp size={14} /> Improve
            </QuickButton>
            <QuickButton onClick={() => navigate('/training/charts')}>
              <Grid3x3 size={14} /> Preflop chart
            </QuickButton>
          </HStack>
        )}
        
        {/* Scenario Action Buttons */}
        {activeScenario && (
          <Box p={3} bg={`${POKER_COLORS.gold}11`} borderRadius="lg" border="1px solid" borderColor={`${POKER_COLORS.gold}44`}>
            <Text color={POKER_COLORS.gold} fontSize="sm" fontWeight="bold" mb={2}>
               What's your play?

            </Text>
            <HStack spacing={2} flexWrap="wrap">
              <PokerButton size="sm" variant="danger" onClick={() => handleScenarioAnswer('fold')}>
                Fold
              </PokerButton>
              <PokerButton size="sm" variant="secondary" onClick={() => handleScenarioAnswer('check')}>
                Check
              </PokerButton>
              <PokerButton size="sm" variant="secondary" onClick={() => handleScenarioAnswer('call')}>
                Call
              </PokerButton>
              <PokerButton size="sm" variant="primary" onClick={() => handleScenarioAnswer('raise')}>
                Raise
              </PokerButton>
              <PokerButton size="sm" variant="primary" onClick={() => handleScenarioAnswer('all-in')}>
                All-In
              </PokerButton>
            </HStack>
          </Box>
        )}
        
        {/* Input Area */}
        <Box>
          <InputGroup size="lg">
            <Input
              ref={inputRef}
              placeholder="Ask me anything about poker..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              bg={`${POKER_COLORS.darkTeal}99`}
              border="2px solid"
              borderColor={`${POKER_COLORS.cyan}44`}
              color="white"
              _placeholder={{ color: 'gray.500' }}
              _focus={{
                borderColor: POKER_COLORS.cyan,
                boxShadow: `0 0 12px ${POKER_COLORS.cyan}44`,
              }}
              pr="50px"
            />
            <InputRightElement>
              <IconButton
                aria-label="Send"
                icon={<Send size={20} />}
                variant="ghost"
                color={POKER_COLORS.cyan}
                isLoading={isLoading}
                onClick={handleSend}
                isDisabled={!input.trim()}
              />
            </InputRightElement>
          </InputGroup>
        </Box>
      </VStack>
    </Container>
  );
};

// ============================================================================
// SESSION STATS PANEL
// ============================================================================

const SessionStatsPanel: React.FC<{ report: TrainingReport }> = ({ report }) => {
  const opt = OPTIMAL_STATS.sixMax;
  
  const getStatColor = (value: number, min: number, max: number) => {
    if (value < min) return POKER_COLORS.darkRed;
    if (value > max) return POKER_COLORS.gold;
    return POKER_COLORS.darkGreen;
  };
  
  return (
    <PokerCard p={4}>
      <Grid templateColumns="repeat(4, 1fr)" gap={3}>
        <Box textAlign="center">
          <Text color="gray.500" fontSize="xs">VPIP</Text>
          <Text color={getStatColor(report.stats.vpip, opt.vpip.min, opt.vpip.max)} fontWeight="bold">
            {report.stats.vpip.toFixed(0)}%
          </Text>
        </Box>
        <Box textAlign="center">
          <Text color="gray.500" fontSize="xs">PFR</Text>
          <Text color={getStatColor(report.stats.pfr, opt.pfr.min, opt.pfr.max)} fontWeight="bold">
            {report.stats.pfr.toFixed(0)}%
          </Text>
        </Box>
        <Box textAlign="center">
          <Text color="gray.500" fontSize="xs">C-Bet</Text>
          <Text color={getStatColor(report.stats.cbetFrequency, opt.cbet.min, opt.cbet.max)} fontWeight="bold">
            {report.stats.cbetFrequency.toFixed(0)}%
          </Text>
        </Box>
        <Box textAlign="center">
          <Text color="gray.500" fontSize="xs">Aggression</Text>
          <Text color={getStatColor(report.stats.aggressionFactor, opt.aggFactor.min, opt.aggFactor.max)} fontWeight="bold">
            {report.stats.aggressionFactor.toFixed(1)}
          </Text>
        </Box>
      </Grid>
    </PokerCard>
  );
};

// ============================================================================
// MESSAGE BUBBLE
// ============================================================================

const MessageBubble: React.FC<{
  message: Message;
  onQuickAction: (action: string) => void;
  onScenarioAnswer: (action: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all-in') => void;
  activeScenario: Scenario | null;
}> = ({ message, onQuickAction, onScenarioAnswer, activeScenario }) => {
  const isUser = message.role === 'user';
  const navigate = useNavigate();
  // Coach answers about ranges, opening hands or short-stack play get a
  // one-tap link to the preflop chart, the reference the advice comes from.
  const chartQuery = (text: string): string | null => {
    const t = text.toLowerCase();
    if (!/\b(range|ranges|preflop|pre-flop|open(ing)? hands?|push|shove|jam|short.?stack|starting hands?)\b/.test(t)) return null;
    const pos = t.match(/\b(utg|utg\+1|hj|lj|mp|co|cutoff|btn|button|sb|small blind|bb|big blind)\b/);
    const posMap: Record<string, string> = { cutoff: 'CO', button: 'BTN', 'small blind': 'SB', 'big blind': 'BB' };
    const position = pos ? (posMap[pos[1]] || pos[1].toUpperCase()) : '';
    const situation = /\b(push|shove|jam|short.?stack)\b/.test(t) ? 'push' : /\b(3-?bet|facing a raise|vs\.? (a )?raise|someone raises?)\b/.test(t) ? 'vs' : 'open';
    const params = new URLSearchParams({ situation });
    if (position) params.set('position', position);
    return `/training/charts?${params.toString()}`;
  };

  if (isUser) {
    return (
      <HStack justify="flex-end">
        <Box
          maxW="80%"
          p={3}
          bg={`${POKER_COLORS.cyan}22`}
          border="1px solid"
          borderColor={`${POKER_COLORS.cyan}44`}
          borderRadius="lg"
          borderBottomRightRadius="sm"
        >
          <Text color="white" fontSize="sm">{message.content}</Text>
        </Box>
      </HStack>
    );
  }
  
  const response = message.response;
  if (!response) return null;
  
  return (
    <Box maxW="95%">
      <PokerCard p={4}>
        <VStack align="start" spacing={3}>
          {/* Title with Priority Badge */}
          {response.title && (
            <HStack>
              <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="md">
                {response.title}
              </Text>
              {response.priority === 'critical' && (
                <Badge bg={`${POKER_COLORS.darkRed}33`} color={POKER_COLORS.darkRed}>
                  CRITICAL
                </Badge>
              )}
            </HStack>
          )}
          
          {/* Content */}
          <Text 
            color="gray.200" 
            fontSize="sm" 
            whiteSpace="pre-wrap"
            lineHeight="1.6"
            sx={{
              '& strong': { color: POKER_COLORS.cyan },
              '& b': { color: POKER_COLORS.cyan },
            }}
          >
            {formatContent(response.content)}
          </Text>

          {(() => {
            const link = chartQuery(`${response.title || ''} ${response.content || ''}`);
            if (!link) return null;
            return (
              <PokerButton size="sm" variant="secondary" onClick={() => navigate(link)}>
                <Grid3x3 size={14} style={{ marginRight: 6 }} /> Open the preflop chart
              </PokerButton>
            );
          })()}

          {/* Details / Math */}
          {response.details && (
            <Box
              p={3}
              bg={`${POKER_COLORS.darkTeal}aa`}
              borderRadius="md"
              w="100%"
              borderLeft="3px solid"
              borderColor={POKER_COLORS.gold}
            >
              <Text color="gray.300" fontSize="xs" whiteSpace="pre-wrap">
                {response.details}
              </Text>
            </Box>
          )}
          
          {response.mathBreakdown && (
            <Box
              p={3}
              bg="blackAlpha.400"
              borderRadius="md"
              w="100%"
            >
              <Text color="gray.400" fontSize="xs" fontWeight="bold" mb={1}>
                 Math:

              </Text>
              <Text color="gray.300" fontSize="xs" whiteSpace="pre-wrap" fontFamily="mono">
                {response.mathBreakdown}
              </Text>
            </Box>
          )}
          
          {/* Leaks Display */}
          {response.leaks && response.leaks.length > 0 && (
            <VStack align="start" spacing={2} w="100%">
              {response.leaks.map((leak, i) => (
                <LeakBadge key={i} leak={leak} />
              ))}
            </VStack>
          )}
          
          {/* Scenario Card Display */}
          {message.scenario && (
            <ScenarioDisplay scenario={message.scenario} />
          )}
          
          {/* Follow-up Questions */}
          {response.followUpQuestions && response.followUpQuestions.length > 0 && (
            <HStack flexWrap="wrap" gap={2} pt={2}>
              {response.followUpQuestions.map((q, i) => (
                <Badge
                  key={i}
                  px={3}
                  py={1}
                  bg={`${POKER_COLORS.cyan}11`}
                  color={POKER_COLORS.cyan}
                  borderRadius="full"
                  cursor="pointer"
                  _hover={{ bg: `${POKER_COLORS.cyan}22` }}
                  onClick={() => onQuickAction(q)}
                  fontSize="xs"
                >
                  {q}
                </Badge>
              ))}
            </HStack>
          )}
          
          {/* Related Terms */}
          {response.relatedTerms && response.relatedTerms.length > 0 && (
            <HStack flexWrap="wrap" gap={1}>
              <Text color="gray.500" fontSize="xs">Related:</Text>
              {response.relatedTerms.map((term, i) => (
                <Badge
                  key={i}
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  cursor="pointer"
                  onClick={() => onQuickAction(`What is ${term}?`)}
                >
                  {term}
                </Badge>
              ))}
            </HStack>
          )}
        </VStack>
      </PokerCard>
    </Box>
  );
};

// ============================================================================
// LEAK BADGE
// ============================================================================

const LeakBadge: React.FC<{ leak: Leak }> = ({ leak }) => {
  const colors = {
    critical: { bg: POKER_COLORS.darkRed, icon: '' },

    major: { bg: POKER_COLORS.gold, icon: '' },

    minor: { bg: POKER_COLORS.cyan, icon: '' },

  };
  
  const { bg, icon } = colors[leak.severity];
  
  return (
    <Box
      p={2}
      bg={`${bg}15`}
      border="1px solid"
      borderColor={`${bg}44`}
      borderRadius="md"
      w="100%"
    >
      <HStack justify="space-between">
        <Text color={bg} fontSize="xs" fontWeight="bold">
          {icon} {leak.title}
        </Text>
        <Badge bg={`${bg}22`} color={bg} fontSize="xs">
          {leak.severity}
        </Badge>
      </HStack>
    </Box>
  );
};

// ============================================================================
// SCENARIO DISPLAY
// ============================================================================

const ScenarioDisplay: React.FC<{ scenario: Scenario }> = ({ scenario }) => {
  const display = formatScenarioForDisplay(scenario);
  
  return (
    <Box
      p={4}
      bg={`${POKER_COLORS.darkTeal}88`}
      borderRadius="lg"
      border="1px solid"
      borderColor={`${POKER_COLORS.cyan}44`}
      w="100%"
    >
      <Grid templateColumns="1fr 1fr" gap={3}>
        <Box>
          <Text color="gray.500" fontSize="xs">Your Hand</Text>
          <Text color="white" fontWeight="bold" fontSize="lg">
            {display.hand}
          </Text>
        </Box>
        <Box>
          <Text color="gray.500" fontSize="xs">Position</Text>
          <Text color="white" fontWeight="bold" fontSize="lg">
            {display.position}
          </Text>
        </Box>
        <Box>
          <Text color="gray.500" fontSize="xs">Board</Text>
          <Text color={POKER_COLORS.gold} fontWeight="bold" fontSize="lg">
            {display.board || '-'}
          </Text>
        </Box>
        <Box>
          <Text color="gray.500" fontSize="xs">Pot</Text>
          <Text color={POKER_COLORS.darkGreen} fontWeight="bold" fontSize="lg">
            {display.pot}
          </Text>
        </Box>
      </Grid>
      
      <Divider my={3} borderColor={`${POKER_COLORS.cyan}33`} />
      
      <Text color="gray.300" fontSize="sm">
        {display.action}
      </Text>
      
      <HStack mt={2} spacing={2}>
        <Badge bg={`${POKER_COLORS.gold}22`} color={POKER_COLORS.gold}>
          {scenario.difficulty}
        </Badge>
        <Badge bg={`${POKER_COLORS.cyan}22`} color={POKER_COLORS.cyan}>
          {scenario.concept.replace(/-/g, ' ')}
        </Badge>
      </HStack>
    </Box>
  );
};

// ============================================================================
// QUICK BUTTON
// ============================================================================

const QuickButton: React.FC<{ 
  onClick: () => void; 
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <Box
    as="button"
    px={3}
    py={1.5}
    bg={`${POKER_COLORS.darkTeal}88`}
    border="1px solid"
    borderColor={`${POKER_COLORS.cyan}33`}
    borderRadius="full"
    color="gray.400"
    fontSize="xs"
    fontWeight="medium"
    display="flex"
    alignItems="center"
    gap={1}
    whiteSpace="nowrap"
    _hover={{
      bg: `${POKER_COLORS.cyan}22`,
      borderColor: `${POKER_COLORS.cyan}66`,
      color: POKER_COLORS.cyan,
    }}
    transition="all 0.2s"
    onClick={onClick}
  >
    {children}
  </Box>
);

// ============================================================================
// HELPERS
// ============================================================================

function formatContent(content: string): string {
  // Convert markdown bold to plain (Chakra will handle styling)
  return content
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\n•/g, '\n•');
}

// ============================================================================
// EXPORT
// ============================================================================

export default PokerCoachPage;